/**
 * Country calling codes.
 *
 * Stored as a delimited string rather than 240 object literals: it is a fraction of the
 * source size, minifies to almost nothing, and the one-time parse below is 240
 * iterations at module load. The parsed rows carry a precomputed lowercase name so
 * searching never lowercases the same 240 strings on every keystroke.
 */

export interface Country {
  /** ISO 3166-1 alpha-2. */
  readonly iso2: string;
  readonly name: string;
  /** Calling code without the leading `+`. */
  readonly dial: string;
  /** Lowercased name, precomputed for search. */
  readonly search: string;
}

// iso2|name|dial
const RAW = `
AF|Afghanistan|93
AL|Albania|355
DZ|Algeria|213
AS|American Samoa|1684
AD|Andorra|376
AO|Angola|244
AI|Anguilla|1264
AG|Antigua and Barbuda|1268
AR|Argentina|54
AM|Armenia|374
AW|Aruba|297
AU|Australia|61
AT|Austria|43
AZ|Azerbaijan|994
BS|Bahamas|1242
BH|Bahrain|973
BD|Bangladesh|880
BB|Barbados|1246
BY|Belarus|375
BE|Belgium|32
BZ|Belize|501
BJ|Benin|229
BM|Bermuda|1441
BT|Bhutan|975
BO|Bolivia|591
BA|Bosnia and Herzegovina|387
BW|Botswana|267
BR|Brazil|55
IO|British Indian Ocean Territory|246
VG|British Virgin Islands|1284
BN|Brunei|673
BG|Bulgaria|359
BF|Burkina Faso|226
BI|Burundi|257
KH|Cambodia|855
CM|Cameroon|237
CA|Canada|1
CV|Cape Verde|238
KY|Cayman Islands|1345
CF|Central African Republic|236
TD|Chad|235
CL|Chile|56
CN|China|86
CO|Colombia|57
KM|Comoros|269
CG|Congo - Brazzaville|242
CD|Congo - Kinshasa|243
CK|Cook Islands|682
CR|Costa Rica|506
CI|Côte d'Ivoire|225
HR|Croatia|385
CU|Cuba|53
CW|Curaçao|599
CY|Cyprus|357
CZ|Czechia|420
DK|Denmark|45
DJ|Djibouti|253
DM|Dominica|1767
DO|Dominican Republic|1809
EC|Ecuador|593
EG|Egypt|20
SV|El Salvador|503
GQ|Equatorial Guinea|240
ER|Eritrea|291
EE|Estonia|372
SZ|Eswatini|268
ET|Ethiopia|251
FK|Falkland Islands|500
FO|Faroe Islands|298
FJ|Fiji|679
FI|Finland|358
FR|France|33
GF|French Guiana|594
PF|French Polynesia|689
GA|Gabon|241
GM|Gambia|220
GE|Georgia|995
DE|Germany|49
GH|Ghana|233
GI|Gibraltar|350
GR|Greece|30
GL|Greenland|299
GD|Grenada|1473
GP|Guadeloupe|590
GU|Guam|1671
GT|Guatemala|502
GG|Guernsey|44
GN|Guinea|224
GW|Guinea-Bissau|245
GY|Guyana|592
HT|Haiti|509
HN|Honduras|504
HK|Hong Kong|852
HU|Hungary|36
IS|Iceland|354
IN|India|91
ID|Indonesia|62
IR|Iran|98
IQ|Iraq|964
IE|Ireland|353
IM|Isle of Man|44
IL|Israel|972
IT|Italy|39
JM|Jamaica|1876
JP|Japan|81
JE|Jersey|44
JO|Jordan|962
KZ|Kazakhstan|7
KE|Kenya|254
KI|Kiribati|686
KW|Kuwait|965
KG|Kyrgyzstan|996
LA|Laos|856
LV|Latvia|371
LB|Lebanon|961
LS|Lesotho|266
LR|Liberia|231
LY|Libya|218
LI|Liechtenstein|423
LT|Lithuania|370
LU|Luxembourg|352
MO|Macao|853
MG|Madagascar|261
MW|Malawi|265
MY|Malaysia|60
MV|Maldives|960
ML|Mali|223
MT|Malta|356
MH|Marshall Islands|692
MQ|Martinique|596
MR|Mauritania|222
MU|Mauritius|230
MX|Mexico|52
FM|Micronesia|691
MD|Moldova|373
MC|Monaco|377
MN|Mongolia|976
ME|Montenegro|382
MS|Montserrat|1664
MA|Morocco|212
MZ|Mozambique|258
MM|Myanmar|95
NA|Namibia|264
NR|Nauru|674
NP|Nepal|977
NL|Netherlands|31
NC|New Caledonia|687
NZ|New Zealand|64
NI|Nicaragua|505
NE|Niger|227
NG|Nigeria|234
NU|Niue|683
NF|Norfolk Island|672
KP|North Korea|850
MK|North Macedonia|389
MP|Northern Mariana Islands|1670
NO|Norway|47
OM|Oman|968
PK|Pakistan|92
PW|Palau|680
PS|Palestine|970
PA|Panama|507
PG|Papua New Guinea|675
PY|Paraguay|595
PE|Peru|51
PH|Philippines|63
PL|Poland|48
PT|Portugal|351
PR|Puerto Rico|1787
QA|Qatar|974
RE|Réunion|262
RO|Romania|40
RU|Russia|7
RW|Rwanda|250
BL|Saint Barthélemy|590
SH|Saint Helena|290
KN|Saint Kitts and Nevis|1869
LC|Saint Lucia|1758
MF|Saint Martin|590
PM|Saint Pierre and Miquelon|508
VC|Saint Vincent and the Grenadines|1784
WS|Samoa|685
SM|San Marino|378
ST|São Tomé and Príncipe|239
SA|Saudi Arabia|966
SN|Senegal|221
RS|Serbia|381
SC|Seychelles|248
SL|Sierra Leone|232
SG|Singapore|65
SX|Sint Maarten|1721
SK|Slovakia|421
SI|Slovenia|386
SB|Solomon Islands|677
SO|Somalia|252
ZA|South Africa|27
KR|South Korea|82
SS|South Sudan|211
ES|Spain|34
LK|Sri Lanka|94
SD|Sudan|249
SR|Suriname|597
SE|Sweden|46
CH|Switzerland|41
SY|Syria|963
TW|Taiwan|886
TJ|Tajikistan|992
TZ|Tanzania|255
TH|Thailand|66
TL|Timor-Leste|670
TG|Togo|228
TK|Tokelau|690
TO|Tonga|676
TT|Trinidad and Tobago|1868
TN|Tunisia|216
TR|Türkiye|90
TM|Turkmenistan|993
TC|Turks and Caicos Islands|1649
TV|Tuvalu|688
UG|Uganda|256
UA|Ukraine|380
AE|United Arab Emirates|971
GB|United Kingdom|44
US|United States|1
UY|Uruguay|598
UZ|Uzbekistan|998
VU|Vanuatu|678
VA|Vatican City|39
VE|Venezuela|58
VN|Vietnam|84
WF|Wallis and Futuna|681
YE|Yemen|967
ZM|Zambia|260
ZW|Zimbabwe|263
`;

export const COUNTRIES: readonly Country[] = RAW.trim()
  .split('\n')
  .map((line) => {
    const [iso2 = '', name = '', dial = ''] = line.split('|');
    return { iso2, name, dial, search: name.toLowerCase() };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

const BY_ISO2 = new Map(COUNTRIES.map((c) => [c.iso2, c]));

/** The default when nothing else is known: the firm's head office. */
export const DEFAULT_COUNTRY: Country = BY_ISO2.get('AE') ?? COUNTRIES[0]!;

export function countryByIso2(iso2: string): Country | undefined {
  return BY_ISO2.get(iso2.toUpperCase());
}

/**
 * Match a country by the name the CMS holds for an office.
 *
 * Used so the form opens on the office's country rather than a hard-coded guess. Exact
 * match only — a fuzzy match here would silently pick the wrong dialling code, which is
 * worse than falling back to the default.
 */
export function countryByName(name: string): Country | undefined {
  const needle = name.trim().toLowerCase();
  return COUNTRIES.find((c) => c.search === needle);
}

/**
 * The flag as regional-indicator characters, derived from the ISO code.
 *
 * No image and no lookup table: each letter maps to its regional indicator symbol, and
 * the pair renders as a flag on platforms that draw them. Windows draws the two letters
 * instead, which is a perfectly readable fallback — hence the code beside it always.
 */
export function flagEmoji(iso2: string): string {
  return iso2
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .split('')
    .map((ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65))
    .join('');
}

/**
 * Search by country name or by calling code, in one box.
 *
 * Typing letters matches the name; typing digits matches the code by prefix, so `24`
 * returns every +24x country. A query with both is matched on either.
 *
 * Results are ranked rather than merely filtered: an exact code, then a name that
 * starts with the query, then a code that starts with it, then a name that merely
 * contains it. Without the ranking, searching "in" puts Ukraine above India.
 */
export function searchCountries(query: string): readonly Country[] {
  const raw = query.trim().toLowerCase();
  if (raw === '') return COUNTRIES;

  const digits = raw.replace(/\D/g, '');
  const letters = raw.replace(/[^a-zÀ-ɏ ]/g, '').trim();

  const scored: { country: Country; score: number }[] = [];

  for (const country of COUNTRIES) {
    let score = 0;

    if (digits !== '') {
      if (country.dial === digits) score = Math.max(score, 5);
      else if (country.dial.startsWith(digits)) score = Math.max(score, 3);
    }

    if (letters !== '') {
      if (country.iso2.toLowerCase() === letters) score = Math.max(score, 5);
      else if (country.search.startsWith(letters)) score = Math.max(score, 4);
      // A word boundary counts as a start: "guinea" should find Equatorial Guinea.
      else if (country.search.includes(` ${letters}`)) score = Math.max(score, 2);
      else if (country.search.includes(letters)) score = Math.max(score, 1);
    }

    if (score > 0) scored.push({ country, score });
  }

  return scored
    .sort((a, b) => b.score - a.score || a.country.name.localeCompare(b.country.name))
    .map((entry) => entry.country);
}
